/**
 * Turning a roof_model into drawable SVG (SD-2.3, DESIGN.md §4).
 *
 * Pure, and kept out of the component, because the geometry is the part worth testing and the JSX
 * is not. The unit suite has no DOM, so anything decided inside a component is decided untested.
 */

import type { RoofModelV1 } from '@/types/solar/roofModel';
import { ROOF_FACE_SHADED, ROOF_FACE_SUNLIT } from './canvasPalette';
import { enuToSvg } from './canvasViewport';
import { roundHalfEven } from './decimal';

type Polygon = ReadonlyArray<readonly [number, number]>;

/**
 * A closed SVG path in user units.
 *
 * Coordinates are rounded to 2 dp. At 27 px per metre that is sub-millimetre, far below anything
 * the geometry layer decides, and it keeps the `d` attribute short enough that React's diff of a
 * 17-plane roof stays cheap.
 */
export function polygonToPath(polygon: Polygon): string {
  if (!polygon || polygon.length < 3) return '';
  const parts: string[] = [];
  for (let i = 0; i < polygon.length; i += 1) {
    const vertex = polygon[i];
    // A single non-finite coordinate makes the WHOLE `d` attribute invalid, so the browser draws
    // nothing and the face vanishes with no error anywhere. Drop the face deliberately instead:
    // a missing face is visible and countable, a silently reshaped one is not. Skipping just the
    // bad vertex was the other option and is worse, because it produces a face that looks
    // plausible and is the wrong shape.
    if (!Number.isFinite(vertex?.[0]) || !Number.isFinite(vertex?.[1])) return '';
    const p = enuToSvg(vertex);
    parts.push(`${i === 0 ? 'M' : 'L'}${round2(p.x)} ${round2(p.y)}`);
  }
  parts.push('Z');
  return parts.join(' ');
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * The ridge lines of one plane, in SVG user units.
 *
 * Ridges are drawn as their own stroked layer rather than as part of the face outline, because
 * DESIGN.md gives them a different stroke and because a ridge is shared between two faces: drawing
 * it per-face would double the stroke and read as a heavier line on exactly the edges the rep uses
 * to judge whether the tool understood the roof.
 */
export function ridgeSegments(plane: RoofModelV1['planes'][number]): Segment[] {
  const poly = plane.polygon_m as unknown as Polygon;
  const out: Segment[] = [];
  for (const edge of plane.edges ?? []) {
    if (edge.kind !== 'ridge') continue;
    const a = poly[edge.from_index];
    const b = poly[edge.to_index];
    if (!a || !b) continue;
    const pa = enuToSvg(a);
    const pb = enuToSvg(b);
    out.push({ x1: round2(pa.x), y1: round2(pa.y), x2: round2(pb.x), y2: round2(pb.y) });
  }
  return out;
}

/**
 * Sunlit or shaded, for the flux-layer-off rendering.
 *
 * The split is the AD-11 shade derate: a plane's own median sunshine hours against the site's
 * unobstructed ceiling. It is deliberately the same quantity the production model derates by, so
 * a face that LOOKS shaded on the canvas is the same face that produces less in the estimate. Two
 * different definitions of "shaded" on one screen is how a rep loses trust in both.
 *
 * Falls back to sunlit when the ratio cannot be computed, because a roof drawn entirely in the
 * shaded fill would imply a measurement that was never made.
 */
export const SHADED_DERATE_THRESHOLD = 0.75;

export type SunshineDenominatorKind = 'site_ceiling' | 'plane_max' | 'none';

/**
 * AD-11's denominator, mirroring `app/services/solar/derate.py`.
 *
 * SD-3.2. This function is the fix for a real defect, not a refactor. Before it, `faceIsShaded`
 * returned `false` whenever `site.max_sunshine_hours` was null — so a roof missing the site
 * ceiling drew EVERY face sunlit. AD-11 §3 requires falling back to the maximum
 * `sunshine_hours_median` across the building's own planes, and says never to silently derate to
 * 1.0 "which would show a shaded roof as unshaded". That was exactly what happened here, and the
 * same bug existed in `snapshot.py`, which was hand-mirrored from this file.
 *
 * The Python side is the authority; this is the mirror. Both are pinned to the same cases by
 * `canvasGeometry.test.ts` and `tests/services/solar/test_derate.py`.
 */
export function sunshineDenominator(
  model: Pick<RoofModelV1, 'planes' | 'site'>
): { value: number | null; kind: SunshineDenominatorKind } {
  const ceiling = model.site?.max_sunshine_hours;
  if (typeof ceiling === 'number' && Number.isFinite(ceiling) && ceiling > 0) {
    return { value: ceiling, kind: 'site_ceiling' };
  }

  let best: number | null = null;
  for (const plane of model.planes ?? []) {
    const median = plane.sun?.sunshine_hours_median;
    if (typeof median === 'number' && Number.isFinite(median) && median > 0) {
      if (best === null || median > best) best = median;
    }
  }
  if (best !== null) return { value: best, kind: 'plane_max' };

  return { value: null, kind: 'none' };
}

/**
 * Whether a face draws shaded.
 *
 * NOT the production derate: this is a 0.75 threshold with no clamp, answering "what colour"
 * rather than "how much energy". It shares only the RATIO with `shade_derate` in derate.py.
 *
 * The second argument is the RESOLVED denominator from `sunshineDenominator`, not the raw site
 * ceiling. Resolve it once per model rather than per plane.
 *
 * An unmeasurable face draws SUNLIT, deliberately, and this is the one place the colour path and
 * the energy path differ: a roof rendered entirely shaded reads as a failed roof rather than as
 * missing data. Production says 1.0 and flags it unmeasured instead.
 */
export function faceIsShaded(
  plane: RoofModelV1['planes'][number],
  denominator: number | null | undefined
): boolean {
  // Derived from the SAME ratio the production derate uses, so the two cannot drift. An
  // unmeasurable face draws SUNLIT — see the note above.
  const { ratio } = solarAccess(plane, denominator);
  if (ratio === null) return false;
  return ratio < SHADED_DERATE_THRESHOLD;
}

/* ── The PRODUCTION derate (SD-3.5, AD-11 §2) ──────────────────────────────────────────────────
 *
 * `app/services/solar/derate.py:solar_access` is the authority; this is the mirror, on the same
 * terms as `sunshineDenominator` above. SD-3.5 is what makes the mirror necessary: the client now
 * recomputes production locally on every layout change (NFR-2 forbids a network call for it), so
 * this number ends up on the canvas beside the figure the backend stored. Two derates would be
 * the canvas disagreeing with the audit record, on a legal surface.
 *
 * It is NOT `faceIsShaded`. That is a 0.75 threshold answering "what colour"; this is a clamped
 * ratio answering "how much energy". They share the ratio and nothing else, which is exactly why
 * the ratio is computed once, here.
 */

/** AD-11's clamp. The floor is not cosmetic: zero would silently delete a face from the total. */
export const DERATE_MIN = 0.3;
export const DERATE_MAX = 1;

export interface SolarAccess {
  /** The raw, unclamped ratio, or null when it could not be measured. */
  ratio: number | null;
  /** The clamped, two-decimal derate production multiplies by. */
  derate: number;
  /**
   * The field that is easy to leave out and matters most. A derate of 1.00 means either "this
   * plane is unobstructed" or "we could not measure it", and those are different claims to put in
   * front of a customer. Without the flag, a data gap reads as a clear roof.
   */
  measured: boolean;
}

/** A usable non-negative number, or null. Mirrors `derate.py:_num`, bools and all. */
function usableNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return value;
}

/**
 * One plane's obstruction reading.
 *
 * The second argument is the RESOLVED denominator from `sunshineDenominator`, not the raw site
 * ceiling — resolve it once per model rather than once per plane.
 *
 * Never derived from flux. AD-11 is explicit: flux embeds tilt and azimuth that PVWatts already
 * models, so a flux-derived derate applies the orientation penalty twice and understates every
 * steep or off-south face.
 */
export function solarAccess(
  plane: Pick<RoofModelV1['planes'][number], 'sun'>,
  denominator: number | null | undefined
): SolarAccess {
  const median = usableNumber(plane?.sun?.sunshine_hours_median);
  const denom = usableNumber(denominator);

  if (median === null || denom === null || denom <= 0) {
    // Unknowable. 1.00 is the only honest multiplier — we cannot claim an obstruction we did not
    // measure — and `measured: false` is what stops it reading as an unobstructed roof.
    return { ratio: null, derate: DERATE_MAX, measured: false };
  }

  const ratio = median / denom;
  return {
    ratio,
    // Two decimals, ties to EVEN — `derate.py` quantizes with no rounding argument, so it takes
    // the decimal context default. See `decimal.ts` for why that is not `toFixed`.
    derate: roundHalfEven(Math.min(DERATE_MAX, Math.max(DERATE_MIN, ratio)), 2),
    measured: true,
  };
}

export function faceStyle(
  plane: RoofModelV1['planes'][number],
  siteMaxSunshineHours: number | null | undefined
): typeof ROOF_FACE_SUNLIT | typeof ROOF_FACE_SHADED {
  return faceIsShaded(plane, siteMaxSunshineHours) ? ROOF_FACE_SHADED : ROOF_FACE_SUNLIT;
}

export interface DrawableFace {
  id: string;
  path: string;
  fill: string;
  stroke: string;
  strokeOpacity: number;
  ridges: Segment[];
  shaded: boolean;
  /** SDR-1.4: the face cannot hold one panel, so it is drawn quieter. Still drawn. */
  muted: boolean;
}

/**
 * Everything the canvas needs to draw one roof, computed once.
 *
 * Returned as a flat array rather than computed inside the render, so the cost is measurable (see
 * `canvasGeometry.perf.test.ts`) and memoisable by the component.
 */
export function drawableFaces(
  model: RoofModelV1 | null | undefined,
  /** SDR-1.4: the faces that can hold a panel. Absent means "not judged", and nothing is muted. */
  usable?: ReadonlySet<string>
): DrawableFace[] {
  if (!model?.planes) return [];
  const ceiling = sunshineDenominator(model).value;
  const out: DrawableFace[] = [];
  for (const plane of model.planes) {
    const path = polygonToPath(plane.polygon_m as unknown as Polygon);
    if (!path) continue;
    const style = faceStyle(plane, ceiling);
    out.push({
      id: plane.id,
      path,
      fill: style.fill,
      stroke: style.stroke,
      strokeOpacity: style.strokeOpacity,
      ridges: ridgeSegments(plane),
      shaded: faceIsShaded(plane, ceiling),
      muted: usable ? !usable.has(plane.id) : false,
    });
  }
  return out;
}

/** All plane polygons, for bounds computation. */
export function polygonsOf(model: RoofModelV1 | null | undefined): Polygon[] {
  return (model?.planes ?? []).map((p) => p.polygon_m as unknown as Polygon);
}
